import { useArtifactViewCtx } from "@/components/artifacts/view/ArtifactViewContext.tsx";
import { addDays, dateOf, todayIn } from "@/shared/dayBounds.ts";
import {
  InstantBookDayGrid,
  type PlacedSlot,
} from "@/components/scheduling/InstantBookDayGrid.tsx";
import { Button } from "@/components/ui/button.tsx";
import { instantBookQueries } from "@/hooks/instantBookQueries.ts";
import type { ArtifactComponent } from "@/types/artifacts-view.ts";
import type { InstantBookColumn } from "@/types/instantBook.ts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

type InstantBookPickerData = Extract<ArtifactComponent, { type: "instantBookPicker" }>;

/**
 * Instant Book's time picker on the kickoff card.
 *
 * Nothing the user does waits on the network. The day, its hours and the
 * meeting all come from what this component already knows — the artifact's
 * placed slot and the meeting's timezone — so paging and placing redraw
 * immediately. Only other people's events need fetching, and they arrive into
 * columns that are already on screen.
 */
export const InstantBookPickerBlock = ({ component }: { component: InstantBookPickerData }) => {
  const { threadId, messageId, artifactId, isReadOnly, patch, patchOne } = useArtifactViewCtx();
  const editable = !isReadOnly && component.editable;
  const queryClient = useQueryClient();
  const { timezone, participantsKey } = component;

  // The day is decided here rather than by the server, so the first render
  // already knows which day it is drawing and every fetch is keyed on a
  // concrete date — asking with "no date" and then with a resolved one made
  // two cache entries for the same day and fetched it twice.
  const [date, setDate] = useState(() => {
    if (component.slot) return dateOf(component.slot.startMsSinceEpoch, timezone);
    // Nothing placed: open on the day the user's window starts. Today is the
    // wrong guess — a meeting asked for next Friday has nothing to do with it,
    // and the grid would show a day whose calendars don't matter. Never before
    // today, which is as far back as paging goes.
    const today = todayIn(timezone);
    const windowStart = component.windowStartDate;
    return windowStart && windowStart > today ? windowStart : today;
  });

  const { data: day, isError } = useQuery(
    instantBookQueries.day(threadId, messageId, artifactId, date, participantsKey),
  );

  // Neighbours, so paging is usually instant. Cached like any other day; the
  // work is wasted only if the user never goes there.
  useEffect(() => {
    for (const neighbour of [addDays(date, -1), addDays(date, 1)]) {
      if (neighbour < todayIn(timezone)) continue;
      void queryClient.prefetchQuery(
        instantBookQueries.day(threadId, messageId, artifactId, neighbour, participantsKey),
      );
    }
  }, [date, timezone, threadId, messageId, artifactId, participantsKey, queryClient]);

  // The people are the same every day, so their columns stay on screen while a
  // new day's events load — the alternative is the grid collapsing to nothing
  // and springing back.
  const lastColumns = useRef<InstantBookColumn[]>([]);
  if (day) lastColumns.current = day.columns;
  const columns = day?.columns ?? lastColumns.current;

  // A placement this client made, which leads the server until the write and
  // its refetch land. Carries the day it belongs to so it can't be drawn on a
  // different one.
  const [pending, setPending] = useState<{ date: string; slot: PlacedSlot } | null>(null);

  const place = async (slot: PlacedSlot, bookOverConflict: boolean) => {
    setPending({ date, slot });
    const minutes = Math.round((slot.endMsSinceEpoch - slot.startMsSinceEpoch) / 60000);
    const patches = [
      {
        path: component.slotEditPath,
        value: { ...slot, ...(bookOverConflict ? { bookOverConflict: true } : {}) },
      },
      // Only when a resize actually changed it. Sending it every time reports a
      // duration edit the user never made, and the assistant is then told to
      // reconcile a change that didn't happen.
      ...(minutes !== component.duration
        ? [{ path: component.durationEditPath, value: minutes }]
        : []),
    ];
    try {
      await patch(patches);
      // Only the day on screen refetches. Invalidating every day the user has
      // paged through sends them all back at once, and each is a live provider
      // round trip.
      await queryClient.invalidateQueries({
        queryKey: instantBookQueries.day(threadId, messageId, artifactId, date, participantsKey)
          .queryKey,
      });
    } finally {
      // Hand back to the artifact's own answer either way; on a rejection the
      // toast carries the reason.
      setPending(null);
    }
  };

  // The artifact's slot is the truth about where the meeting is; the day being
  // viewed only decides whether it is drawn here.
  const placedDate = component.slot ? dateOf(component.slot.startMsSinceEpoch, timezone) : null;
  const serverPlacement = placedDate === date && component.slot ? component.slot : null;

  return (
    <div className="flex flex-col gap-3">
      {component.noMutualTime ? (
        <div className="flex flex-col gap-2 rounded-md bg-muted px-3 py-2">
          <p className="text-sm">
            There's no time when everyone is free. You can have me coordinate over email instead, or
            pick a time here anyway and I'll book over the conflict.
          </p>
          {editable ? (
            <div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void patchOne(component.autoScheduleEditPath, false)}
                data-statsig-name="instant-book-switch-to-email"
              >
                Coordinate over email instead
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {isError && !day ? (
        <p className="text-sm text-muted-foreground">
          Couldn't load everyone's calendars just now.
        </p>
      ) : null}

      <InstantBookDayGrid
        date={date}
        timezone={timezone}
        durationMinutes={component.duration}
        day={day}
        columns={columns}
        calendarsLoading={!day}
        placedSlot={pending?.date === date ? pending.slot : serverPlacement}
        editable={editable}
        onDateChange={setDate}
        onPlace={(slot, bookOverConflict) => void place(slot, bookOverConflict)}
      />
    </div>
  );
};
